import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { productService, Product } from "../../services/productService";
import { categoryService, Category } from "../../services/categoryService";
import { useProductFilters } from "../../hooks/useProductFilters";
import ProductFilters from "../../components/catalog/ProductFilters";
import Footer from "../../components/layout/Footer";
import { ProductGridSkeleton } from "../../components/common/LoadingSkeleton";
import Pagination from "../../components/common/Pagination";
import LazyImage from "../../components/common/LazyImage";

const Catalog: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, updateFiltersBase] = useProductFilters({
    page: 1,
    limit: 20,
  });

  const updateFilters = (
    newFilters: typeof filters,
    shouldNavigate?: boolean
  ) => {
    updateFiltersBase(newFilters);
    // If shouldNavigate is true, scroll to top and ensure we're on catalog page
    if (shouldNavigate) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 4,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Ensure limit is always set
      const queryFilters = {
        ...filters,
        page: filters.page || 1,
        limit: filters.limit || 4,
      };
      console.log("Loading products with filters:", queryFilters);
      const response = await productService.getAll(queryFilters);
      console.log("Products response:", response); // Debug log
      if (response && response.products && response.pagination) {
        setProducts(response.products);
        setPagination(response.pagination);
      } else {
        console.error("Invalid response structure:", response);
        setError("استجابة غير صحيحة من الخادم");
      }
    } catch (err: any) {
      console.error("Error loading products:", err);
      setError(err.message || "فشل تحميل المنتجات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="catalog-page bg-background-light dark:bg-background-dark min-h-screen">
      {/* Desktop Header */}
      <div className="hidden md:block bg-background-light dark:bg-background-dark border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-[#0d171b] dark:text-white">
            كتالوج المنتجات
          </h1>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden flex items-center bg-background-light dark:bg-background-dark p-4 pb-2 justify-between sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-[#0d171b] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] flex-1 text-center">
          كتالوج المنتجات
        </h2>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block">
            <div className="sticky top-4">
              <ProductFilters
                categories={categories}
                filters={filters}
                onFiltersChange={updateFilters}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters - Mobile Layout */}
            <div className="lg:hidden mb-6">
              <ProductFilters
                categories={categories}
                filters={filters}
                onFiltersChange={updateFilters}
              />
            </div>

            {loading && <ProductGridSkeleton count={8} />}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <p className="text-red-600 dark:text-red-400 font-semibold mb-2">
                  خطأ في تحميل المنتجات
                </p>
                <p className="text-red-600 dark:text-red-400 text-sm">
                  {error}
                </p>
                <p className="text-red-600 dark:text-red-400 text-xs mt-2">
                  تأكد من أن الخادم يعمل على{" "}
                  {process.env.REACT_APP_API_URL ||
                    "http://localhost:3000/api/v1"}
                </p>
              </div>
            )}

            {!loading && !error && (
              <>
                <div className="products-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      to={`/product/${product.id}`}
                      className="product-card border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden hover:shadow-lg transition bg-white dark:bg-slate-800 group"
                    >
                      {product.images && product.images.length > 0 ? (
                        <LazyImage
                          src={product.images[0].imageUrl}
                          alt={product.images[0].altText || product.name}
                          className="w-full object-cover group-hover:scale-105 transition-transform duration-300"
                          aspectRatio="aspect-square"
                        />
                      ) : (
                        <div className="w-full aspect-square bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                          <span className="text-slate-400">لا توجد صورة</span>
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="text-lg md:text-xl font-bold mb-2 text-[#0d171b] dark:text-white leading-tight line-clamp-2">
                          {product.name}
                        </h3>
                        {product.description && (
                          <p className="text-slate-600 dark:text-slate-400 mb-3 text-sm md:text-base leading-relaxed line-clamp-2">
                            {product.description}
                          </p>
                        )}
                        <p className="text-xl md:text-2xl font-bold text-primary leading-tight">
                          {Number(product.basePrice).toFixed(2)} د.ج
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>

                {!loading && !error && products.length === 0 && (
                  <div className="text-center py-12">
                    <div className="max-w-md mx-auto">
                      <svg
                        className="w-24 h-24 mx-auto text-slate-300 dark:text-slate-600 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <h3 className="text-xl font-bold text-[#0d171b] dark:text-white mb-2">
                        لم يتم العثور على منتجات
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400 mb-4">
                        لا توجد منتجات تطابق الفلاتر المحددة
                      </p>
                      <button
                        onClick={() => updateFilters({ page: 1, limit: 20 })}
                        className="px-6 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition"
                      >
                        مسح جميع الفلاتر
                      </button>
                    </div>
                  </div>
                )}

                {pagination.totalPages > 1 && (
                  <div className="mt-8">
                    <Pagination
                      currentPage={pagination.page}
                      totalPages={pagination.totalPages}
                      onPageChange={(page) => {
                        console.log(
                          "Page change requested:",
                          page,
                          "Current filters:",
                          filters
                        );
                        updateFilters({
                          ...filters,
                          page,
                          limit: filters.limit || 4,
                        });
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Catalog;
